(() => {
  if (customElements.get('zz-flex-bg')) return;

  // One observer for all background videos; no scroll or resize polling.
  // A positive threshold also catches slides moving from edge contact (0 area)
  // into view: isIntersecting alone can stay true throughout that transition.
  const visibility = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const block = entry.target;
        block.inView =
          entry.isIntersecting &&
          entry.intersectionRect.width > 0 &&
          entry.intersectionRect.height > 0;
        block.syncVideo();
      }
    },
    { threshold: [0, 0.01] }
  );

  class ZZFlexBackground extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.token = (this.token || 0) + 1;
      this.inView = false;
      this.media = this.querySelector(':scope > .zz-flex-bg-media');
      this.video = this.media?.querySelector(':scope > video');
      if (this.dataset.link || this.dataset.linkMap)
        this.listen(this, 'click', (event) => this.navigate(event));
      if (!this.video) return;
      this.mobile = matchMedia('(max-width: 1023px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.listen(this.mobile, 'change', () => this.syncVideo());
      this.listen(this.motion, 'change', () => this.syncVideo());
      this.listen(document, 'visibilitychange', () => this.syncVideo());
      this.listen(window, 'pagehide', () => {
        this.pageHidden = true;
        this.syncVideo();
      });
      this.listen(window, 'pageshow', () => {
        this.pageHidden = false;
        this.syncVideo();
      });
      if (navigator.connection?.addEventListener)
        this.listen(navigator.connection, 'change', () => this.syncVideo());
      this.listen(this.video, 'playing', () => {
        if (!this.canPlay()) {
          this.video.pause();
          return;
        }
        this.media.classList.add('is-playing');
      });
      this.listen(this.video, 'error', () => {
        this.failedSource = this.video.getAttribute('src');
        this.media.classList.remove('is-playing');
      });
      // Observe only containing slides, not the entire page or descendant subtree.
      // Swiper supplies active classes after DOMContentLoaded and during transitions.
      this.slideObserver = new MutationObserver(() => this.syncVideo());
      this.slides = [];
      for (
        let parent = this.parentElement;
        parent;
        parent = parent.parentElement
      ) {
        if (
          parent.classList.contains('swiper-slide') ||
          parent.parentElement?.matches('.swiper-wrapper, .zz-swiper-wrapper')
        ) {
          this.slides.push(parent);
          this.slideObserver.observe(parent, {
            attributes: true,
            attributeFilter: ['class', 'aria-hidden'],
          });
        }
      }
      visibility.observe(this);
    }

    listen(target, event, callback) {
      target.addEventListener(event, callback, { signal: this.events.signal });
    }

    canPlay() {
      return (
        this.isConnected &&
        this.inView &&
        !document.hidden &&
        !this.pageHidden &&
        !this.motion.matches &&
        !navigator.connection?.saveData &&
        !(this.dataset.display === 'pc_only' && this.mobile.matches) &&
        !(this.dataset.display === 'mobile_only' && !this.mobile.matches) &&
        this.slides.every((slide) => {
          const swiper = slide.parentElement?.parentElement?.swiper;
          // Some layout swipers are static grids on desktop and initialize only
          // on mobile. Visible grid cards must keep their background playback.
          return (
            (!swiper ||
              swiper.destroyed ||
              slide.classList.contains('swiper-slide-active')) &&
            slide.getAttribute('aria-hidden') !== 'true'
          );
        })
      );
    }

    syncVideo() {
      if (!this.video || !this.events) return;
      const token = ++this.token;
      clearTimeout(this.playTask);
      this.cancelPosterWait?.();
      const video = this.video;
      if (!this.canPlay()) {
        video.pause();
        return;
      }
      const src = this.mobile.matches
        ? video.dataset.mobileSrc
        : video.dataset.desktopSrc;
      if (!src || src === this.failedSource) {
        video.pause();
        this.media.classList.remove('is-playing');
        return;
      }
      const play = () => {
        if (token !== this.token || !this.canPlay()) return;
        this.playTask = setTimeout(() => {
          if (token !== this.token || !this.canPlay()) return;
          if (video.getAttribute('src') !== src) {
            video.pause();
            this.media.classList.remove('is-playing');
            video.src = src;
            video.load();
          }
          // Do not restart a non-looping video merely because it re-enters the viewport.
          if (video.ended && !video.loop) return;
          video.muted = true;
          video.play().catch(() => {
            /* Keep the responsive poster on autoplay denial. */
          });
        }, 0);
      };
      const poster = this.media.querySelector('img');
      if (!poster || poster.complete || video.getAttribute('src') === src) {
        play();
        return;
      }
      // Give the poster a head start, but a stalled image must not prevent playback.
      const finish = () => {
        this.cancelPosterWait?.();
        play();
      };
      const timeout = setTimeout(finish, 1500);
      this.cancelPosterWait = () => {
        clearTimeout(timeout);
        poster.removeEventListener('load', finish);
        poster.removeEventListener('error', finish);
        this.cancelPosterWait = null;
      };
      poster.addEventListener('load', finish, { once: true });
      poster.addEventListener('error', finish, { once: true });
    }

    navigate(event) {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.target.closest('zz-flex-bg') !== this
      )
        return;
      // Nested links/buttons/popups keep their own action instead of the background's.
      if (
        event.target.closest(
          'a, button, input, select, textarea, label, summary, [role="button"], [role="link"], [contenteditable], [data-zz-modal-target], [data-zz-module-target], zz-video-button, zz-video-popup'
        )
      )
        return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      if (
        this.dataset.linkMap &&
        typeof window.navigateSiteJump === 'function'
      ) {
        event.preventDefault();
        // Keep the original overlay as the HPM attribution element.
        window.navigateSiteJump(
          this.dataset.linkMap,
          this.querySelector(':scope > .bg-responsive-block-layout')
        );
      } else if (this.dataset.link) {
        window.location.assign(this.dataset.link);
      }
    }

    disconnectedCallback() {
      ++this.token;
      clearTimeout(this.playTask);
      this.cancelPosterWait?.();
      visibility.unobserve(this);
      this.slideObserver?.disconnect();
      this.events?.abort();
      this.events = null;
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
      }
      this.media?.classList.remove('is-playing');
      this.video = null;
      this.failedSource = null;
      this.pageHidden = false;
    }
  }
  customElements.define('zz-flex-bg', ZZFlexBackground);
})();
